import React from "react";
import Select, { createFilter } from "react-select";
import PanelState from "../MainComponents/PanelState";

/**
 * Task 37, B2 — matching each package the reseller bills to products in their own ConnectWise
 * catalogue.
 *
 * Every package needs two: one for licences and one for storage. Either being absent is what makes
 * the mapping incomplete, and an incomplete mapping is what stops sync being switched on.
 *
 * Presentational. **Completeness is decided by the server** and simply displayed here — working it
 * out again in the browser would be a second opinion that could disagree with the one that actually
 * gates enabling.
 *
 * NO MODAL. Each row edits in place.
 */
export default function ProductMappingGrid({
    summary, products, saving, savingKey, onSave, loading, error, onRetry,
    // A master administrator working on another reseller: mappings are shown, not changed. The
    // server refuses the save; the pickers are disabled and Save is not offered.
    readOnly,
}) {
    const packages = (summary && summary.packages) || [];
    const missing = (summary && summary.missingMappings) || [];

    // Loading and failure are shown in place of the list, not alongside it, and both are told apart
    // from genuine emptiness — a failed fetch used to render "There is nothing to map yet."
    if (packages.length === 0) {
        return (
            <PanelState
                busy={loading}
                error={error}
                title="There is nothing to map yet."
                detail="Packages appear here once you have attached customers to an agreement, since those are the packages that will actually be billed."
                actionLabel={error ? "Try again" : undefined}
                busyLabel="Trying…"
                onAction={error ? onRetry : undefined}
            />
        );
    }

    return (
        <div>
            <p className="text-muted" style={{ maxWidth: 820 }}>
                {readOnly
                    ? "The products this reseller bills each package as. Only the reseller can change these."
                    : "Match each package you bill to a product in your own ConnectWise catalogue. Both a licence and a storage product are needed before sync can be switched on."}
            </p>

            {missing.length > 0 && (
                <div className="alert alert-warning py-2" style={{ fontSize: 14, maxWidth: 820 }}>
                    <div className="fw-semibold mb-1">Still to map before sync can be enabled:</div>
                    <ul className="mb-0">
                        {missing.map((m) => (
                            <li key={m}>{m}</li>
                        ))}
                    </ul>
                </div>
            )}

            <table className="table table-sm align-middle">
                <thead>
                    <tr>
                        <th>Package</th>
                        {/* Room for a product name to be read; it wraps beyond this. */}
                        <th style={{ minWidth: 260 }}>Licence product</th>
                        <th style={{ minWidth: 260 }}>Storage product</th>
                        <th style={{ width: 110 }} />
                    </tr>
                </thead>
                <tbody>
                    {packages.map((p) => (
                        <ProductMappingRow
                            key={p.sourceProductKey}
                            row={p}
                            products={products}
                            saving={saving && savingKey === p.sourceProductKey}
                            disabled={saving || readOnly}
                            readOnly={readOnly}
                            onSave={onSave}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * One package's two mappings.
 *
 * Holds its own draft so a half-made change on one row cannot disturb another — each row saves
 * independently, which also keeps a failure on one from losing the others' edits.
 */
function ProductMappingRow({ row, products, saving, disabled, readOnly, onSave }) {
    const [licenceId, setLicenceId] = React.useState(row.licenceCwProductId || "");
    const [storageId, setStorageId] = React.useState(row.storageCwProductId || "");

    // Reset when the server sends a fresh picture, so a reload is not silently overwritten by a
    // draft the reseller has forgotten about.
    React.useEffect(() => {
        setLicenceId(row.licenceCwProductId || "");
        setStorageId(row.storageCwProductId || "");
    }, [row.licenceCwProductId, row.storageCwProductId]);

    const changed =
        String(licenceId || "") !== String(row.licenceCwProductId || "") ||
        String(storageId || "") !== String(row.storageCwProductId || "");

    function describe(id) {
        const chosen = products.find((p) => String(p.id) === String(id));
        return chosen || null;
    }

    return (
        <tr className={row.isComplete ? undefined : "table-light"}>
            <td>
                {row.packageName}
                {!row.isComplete && (
                    <div className="text-muted" style={{ fontSize: 12 }}>Incomplete</div>
                )}
            </td>
            <td>
                <ProductPicker
                    value={licenceId}
                    products={products}
                    disabled={disabled}
                    onChange={setLicenceId}
                />
            </td>
            <td>
                <ProductPicker
                    value={storageId}
                    products={products}
                    disabled={disabled}
                    onChange={setStorageId}
                />
            </td>
            <td className="text-end">
                {!readOnly && (
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={disabled || !changed}
                    onClick={() => {
                        const licence = describe(licenceId);
                        const storage = describe(storageId);
                        onSave({
                            sourceProductKey: row.sourceProductKey,
                            licenceCwProductId: licenceId ? Number(licenceId) : null,
                            licenceCwProductIdentifier: licence ? licence.identifier : null,
                            licenceCwProductDescription: licence ? licence.name : null,
                            storageCwProductId: storageId ? Number(storageId) : null,
                            storageCwProductIdentifier: storage ? storage.identifier : null,
                            storageCwProductDescription: storage ? storage.name : null,
                        });
                    }}
                >
                    {saving ? "Saving…" : "Save"}
                </button>
                )}
            </td>
        </tr>
    );
}

/**
 * Task 37 N8 — a product dropdown you can type into to search.
 *
 * The list is the reseller's whole ConnectWise catalogue, which a plain dropdown does not survive
 * beyond a short one. This used to be a search box above a plain select, which gave every row two
 * stacked fields per product and four search boxes for two packages; the client asked for one
 * searchable dropdown instead. react-select is already a dependency (RestoreTree.js).
 *
 * THE CHOSEN PRODUCT IS WHAT IS SHOWN, whatever has been typed. Searching only narrows the list
 * that opens, never the value on screen, so saving always maps the product the reseller can see.
 */
const NOT_MAPPED = { value: "", label: "Not mapped" };

// Searched on the text the reseller can read (identifier and name), not on the hidden id.
const productFilter = createFilter({ stringify: (option) => option.label });

const pickerStyles = {
    control: (base) => ({ ...base, minHeight: 31, fontSize: 14 }),
    // A grid of one cell, shared by the value and the search box. With the value wrapping (below)
    // and react-select's default wrapping flex row, the search box fell onto a line of its own and
    // left an empty row under every long name. The two never show at once — react-select hides the
    // value while something is typed — so they can share the space.
    valueContainer: (base) => ({ ...base, padding: "0 8px", display: "grid" }),
    input: (base) => ({ ...base, gridArea: "1 / 1" }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    // Long product names wrap rather than being cut off. react-select positions the value
    // absolutely and on one line by default, which is what clipped them.
    singleValue: (base) => ({
        ...base,
        gridArea: "1 / 1",
        position: "static",
        transform: "none",
        whiteSpace: "normal",
        maxWidth: "none",
        overflow: "visible",
    }),
    menu: (base) => ({ ...base, zIndex: 5, fontSize: 14 }),
};

function describeProduct(p) {
    return p.identifier ? `${p.identifier} — ${p.name}` : p.name;
}

function ProductPicker({ value, products, disabled, onChange }) {
    const options = [NOT_MAPPED].concat(
        products.map((p) => ({ value: String(p.id), label: describeProduct(p) })));

    // A mapped product no longer in the catalogue is still named rather than shown as "Not
    // mapped", which would misreport what is saved.
    const selected = !value
        ? NOT_MAPPED
        : options.find((o) => o.value === String(value))
            || { value: String(value), label: `Product ${value} (not in your catalogue)` };

    return (
        <Select
            options={options}
            value={selected}
            isDisabled={disabled}
            isSearchable
            placeholder="Search products"
            filterOption={productFilter}
            noOptionsMessage={() => "No products match that search"}
            menuPlacement="auto"
            styles={pickerStyles}
            onChange={(option) => onChange(option ? option.value : "")}
        />
    );
}
